import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import clsx from 'clsx'
import {isEmpty} from 'lodash'
import {FC, useEffect, useMemo, useRef} from 'react'
import {KTSVG} from '../../../../_gori/helpers'

type TrendDirection = 'up' | 'down'

export type CarrierBreakoutItem = {
  name: string
  carrier: string
  value: number
}

const DEFAULT_CHART_COLOR = '#15569C'

interface TrendBadgeProps {
  percent: number
  direction: TrendDirection
}

const TrendBadge: FC<TrendBadgeProps> = ({percent, direction}) => {
  const isPositive = direction === 'up'
  const icon = isPositive
    ? 'media/gori/organizations/trend-arrow-up.svg'
    : 'media/gori/organizations/trend-arrow-down.svg'

  return (
    <span
      className={clsx(
        'metric-card__trend badge',
        isPositive ? 'metric-card__trend-success' : 'badge-light-danger'
      )}
    >
      <span>{Math.abs(percent).toFixed(1)}%</span>
      <KTSVG path={icon} />
    </span>
  )
}

interface MetricCardProps {
  title: string
  totalValue?: number
  periodLabel?: string
  trendPercent?: number
  trendDirection?: TrendDirection
  breakoutTitle?: string
  carrierBreakout: CarrierBreakoutItem[]
  className?: string
  chartColor?: string
  chartValueFormat?: string
  valuePrefix?: string
  valueSuffix?: string
  valueDecimals?: number
}

const MetricCard: FC<MetricCardProps> = ({
  title = '',
  totalValue = 0,
  periodLabel = 'YTD',
  trendPercent = 0,
  trendDirection = 'up',
  breakoutTitle = '',
  carrierBreakout = [],
  chartColor = DEFAULT_CHART_COLOR,
  chartValueFormat = '#.#a',
  valuePrefix = '',
  valueSuffix = '',
  valueDecimals = 0,
  className,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartIdRef = useRef(`metric-chart-${Math.random().toString(36).slice(2, 11)}`)
  const chartId = chartIdRef.current

  const formattedTotalValue = useMemo(
    () =>
      `${valuePrefix}${totalValue.toLocaleString(undefined, {
        minimumFractionDigits: valueDecimals,
        maximumFractionDigits: valueDecimals,
      })}${valueSuffix}`,
    [totalValue, valueDecimals, valuePrefix, valueSuffix]
  )

  useEffect(() => {
    if (!chartRef.current || isEmpty(carrierBreakout)) return

    const root = am5.Root.new(chartId)
    root.setThemes([am5themes_Animated.new(root)])
    root._logo?.dispose()

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        width: am5.percent(100),
        paddingTop: 40,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
      })
    )
    chart.root.dom.style.width = '100%'
    chart.root.dom.style.height = '150px'

    const isSingleBar = carrierBreakout.length === 1
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 10,
      cellStartLocation: isSingleBar ? 0.4 : 0.05,
      cellEndLocation: isSingleBar ? 0.6 : 0.95,
    })
    xRenderer.labels.template.setAll({
      fontSize: 12,
      fontWeight: '500',
      fill: am5.color('#94A3B8'),
      paddingTop: 10,
    })
    xRenderer.grid.template.set('visible', false)

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'name',
        renderer: xRenderer,
      })
    )

    const yRenderer = am5xy.AxisRendererY.new(root, {})
    yRenderer.labels.template.set('visible', false)
    yRenderer.grid.template.set('visible', false)

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yRenderer,
        extraMax: 0.2,
      })
    )
    yAxis.set('numberFormatter', am5.NumberFormatter.new(root, {numberFormat: '#,###'}))

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis,
        yAxis,
        valueYField: 'value',
        categoryXField: 'name',
      })
    )

    series.columns.template.setAll({
      width: am5.percent(isSingleBar ? 70 : 90),
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      fill: am5.color(chartColor),
      stroke: am5.color(chartColor),
    })

    series.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: `{valueY.formatNumber("${chartValueFormat}")}`,
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6,
          fontSize: 12,
          fontWeight: '500',
          fill: am5.color('#5E6278'),
        }),
      })
    )

    xAxis.data.setAll(carrierBreakout)
    series.data.setAll(carrierBreakout)

    series.appear(800)
    chart.appear(800, 100)

    return () => {
      root.dispose()
    }
  }, [carrierBreakout, chartColor, chartValueFormat, chartId])

  return (
    <div className={clsx('card shadow-sm metric-card', className)}>
      <div className='card-body d-flex flex-column metric-card__body'>
        <div className='d-flex flex-column metric-card__header'>
          <div className='metric-card__title'>{title}</div>
          <div className='d-flex align-items-center justify-content-between gap-5 flex-wrap'>
            <div className='d-flex align-items-baseline gap-5'>
              <span className='metric-card__value'>{formattedTotalValue}</span>
              <span className='metric-card__period'>{periodLabel.toUpperCase()}</span>
            </div>
            <TrendBadge percent={trendPercent} direction={trendDirection} />
          </div>
        </div>

        <div className='d-flex flex-column metric-card__breakout-container'>
          <div className='metric-card__breakout-title'>{breakoutTitle}</div>
          <div ref={chartRef} id={chartId} className='metric-card__chart w-100' />
        </div>
      </div>
    </div>
  )
}

export {MetricCard}
